import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { aiPipelineApi, type AiPromptBody, type AiPromptVersion, type AiPromptView } from '@/api/aiPipeline';
import { Button } from '@/components/ui/Button';
import { Card, CardTitle } from '@/components/ui/Card';
import { useConfirm } from '@/components/ui/ConfirmProvider';
import { Input } from '@/components/ui/Input';
import { Pill } from '@/components/ui/Pill';
import { formatDateTime, lintPrompt } from '@/lib/aiPipeline';
import { apiErrorCode } from '@/lib/apiErrorCode';
import { cn } from '@/lib/utils';

import { ApiErrorText, NoteLine, type Note } from './notices';

/**
 * Промпты этапов (договор §4). Правка — новая версия, прежняя уходит в архив;
 * «Вернуть умолчание» — тоже архив. Сохранение шлёт номер версии, которую правили:
 * на `prompt_stale` текст остаётся в полях, а «Перечитать» подтягивает свежую.
 *
 * ⚠️ Промпт общий для всех следующих элементов: уже сделанные проверки не
 * пересчитываются, их перезапускают у нужного элемента.
 */

const LABEL = 'mb-1.5 block text-xs font-semibold uppercase tracking-label text-on-surface-variant';
const AREA =
  'app-field w-full rounded-button bg-surface-container-lowest px-4 py-3 font-mono text-xs leading-relaxed text-on-surface shadow-soft focus:shadow-ambient focus:outline-none';
const PRE = 'max-h-80 overflow-auto whitespace-pre-wrap break-words rounded-xl bg-surface-container px-3 py-2 font-mono text-xs';

export function PromptsTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['ai-pipeline', 'prompts'], queryFn: () => aiPipelineApi.listPrompts() });
  const [key, setKey] = useState('');
  const [search, setSearch] = useState('');
  const [flash, setFlash] = useState('');

  const list = useMemo(() => [...(q.data ?? [])].sort((a, b) => a.key.localeCompare(b.key)), [q.data]);
  const needle = search.trim().toLowerCase();
  const shown = list.filter((p) => !needle || p.key.toLowerCase().includes(needle));
  const selected = list.find((p) => p.key === key);

  const reload = (msg?: string) => {
    setFlash(msg ?? '');
    void qc.invalidateQueries({ queryKey: ['ai-pipeline', 'prompt-versions'] });
    void q.refetch();
  };

  return (
    <div data-ai-prompts className="grid items-start gap-4 lg:grid-cols-[minmax(0,300px)_minmax(0,1fr)]">
      <Card className="space-y-3">
        <CardTitle>{t('adminAiPipeline.prompts.keys')}</CardTitle>
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('adminAiPipeline.prompts.search')} spellCheck={false} />
        {q.isLoading ? <p className="text-sm text-on-surface-variant">{t('adminAiPipeline.common.loading')}</p> : null}
        {q.isError ? <ApiErrorText error={q.error} /> : null}
        <div className="flex max-h-[70vh] flex-col gap-1 overflow-auto">
          {shown.map((p) => (
            <button
              key={p.key}
              type="button"
              data-ai-prompt-key={p.key}
              aria-pressed={p.key === key}
              onClick={() => {
                setKey(p.key);
                setFlash('');
              }}
              className={cn(
                'flex items-center justify-between gap-2 rounded-xl px-3 py-2 text-left text-sm transition',
                p.key === key ? 'bg-primary-fixed font-semibold' : 'hover:bg-surface-container',
              )}
            >
              <span className="min-w-0 break-all font-mono text-xs">{p.key}</span>
              {p.current ? <Pill variant="success">{`v${p.current.version}`}</Pill> : <Pill>{t('adminAiPipeline.prompts.default')}</Pill>}
            </button>
          ))}
        </div>
      </Card>

      <div className="min-w-0 space-y-3">
        {flash ? <NoteLine note={{ tone: 'ok', text: flash }} /> : null}
        {selected ? (
          <PromptEditor
            key={`${selected.key}:${selected.current?.id ?? 'default'}:${selected.current?.version ?? 0}`}
            prompt={selected}
            onSaved={(msg) => reload(msg)}
            onReread={() => reload()}
          />
        ) : (
          <Card>
            <p className="text-sm text-on-surface-variant">{t('adminAiPipeline.prompts.pick')}</p>
          </Card>
        )}
      </div>
    </div>
  );
}

function Placeholders({ list }: { list: string[] | null }) {
  const { t } = useTranslation();
  if (!list?.length) return <span className="text-on-surface-variant">{t('adminAiPipeline.prompts.none')}</span>;
  return (
    <span className="inline-flex flex-wrap gap-1">
      {list.map((n) => (
        <code key={n} className="rounded-md bg-surface-container px-1.5 py-0.5 font-mono text-xs">
          {`{{${n}}}`}
        </code>
      ))}
    </span>
  );
}

type EditorProps = { prompt: AiPromptView; onSaved: (msg: string) => void; onReread: () => void };

function PromptEditor({ prompt: p, onSaved, onReread }: EditorProps) {
  const { t, i18n } = useTranslation();
  const confirm = useConfirm();
  const cur = p.current;
  const [system, setSystem] = useState(cur ? cur.system : p.system);
  const [userTemplate, setUserTemplate] = useState(cur ? cur.user_template : p.user_template);
  const [model, setModel] = useState(cur?.model ?? '');
  const [temp, setTemp] = useState(String(cur ? cur.temperature : (p.temperature ?? 0)));
  const [note, setNote] = useState<Note>(null);
  const [error, setError] = useState<unknown>(null);
  const [showHistory, setShowHistory] = useState(false);
  const lint = lintPrompt(p, system, userTemplate);

  const history = useQuery({
    queryKey: ['ai-pipeline', 'prompt-versions', p.key],
    queryFn: () => aiPipelineApi.promptVersions(p.key),
    enabled: showHistory,
  });
  const save = useMutation({
    mutationFn: (body: AiPromptBody) =>
      cur ? aiPipelineApi.updatePrompt(cur.id, { ...body, version: cur.version }) : aiPipelineApi.createPrompt({ key: p.key, ...body }),
    onSuccess: (row) => onSaved(t('adminAiPipeline.prompts.saved', { key: p.key, n: row?.version ?? '' })),
    onError: (e) => setError(e),
  });
  const archive = useMutation({
    mutationFn: (promptId: string) => aiPipelineApi.archivePrompt(promptId),
    onSuccess: () => onSaved(t('adminAiPipeline.prompts.archived', { n: cur?.version ?? '', key: p.key })),
    onError: (e) => setError(e),
  });

  const fill = (v: { system: string; user_template: string; model?: string; temperature?: number }) => {
    setSystem(v.system ?? '');
    setUserTemplate(v.user_template ?? '');
    setModel(v.model ?? '');
    setTemp(String(v.temperature ?? p.temperature ?? 0));
  };

  const onSave = () => {
    setError(null);
    setNote(null);
    const raw = temp.trim();
    const value = raw === '' ? null : Number(raw);
    if (value !== null && (!Number.isFinite(value) || value < 0 || value > 2)) {
      setNote({ tone: 'hint', text: t('adminAiPipeline.prompts.tempInvalid') });
      return;
    }
    save.mutate({ system, user_template: userTemplate, model: model.trim(), temperature: value });
  };

  const onArchive = async () => {
    if (!cur) return;
    setError(null);
    setNote(null);
    const ok = await confirm({
      title: t('adminAiPipeline.prompts.archiveTitle'),
      message: t('adminAiPipeline.prompts.archiveMessage', { n: cur.version }),
      confirmLabel: t('adminAiPipeline.prompts.archiveConfirm'),
      danger: true,
    });
    if (ok) archive.mutate(cur.id);
  };

  const code = apiErrorCode(error);
  const stale = code === 'prompt_stale' || code === 'prompt_exists';
  const notes: string[] = [];
  if (p.key.startsWith('check.')) notes.push(t('adminAiPipeline.prompts.checkNote'));
  if (p.key.startsWith('type_spec.')) notes.push(t('adminAiPipeline.prompts.typeSpecNote'));

  return (
    <Card className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="min-w-0 break-all font-mono text-base font-semibold">{p.key}</h3>
        {cur ? (
          <Pill variant="success">{t('adminAiPipeline.prompts.current', { n: cur.version })}</Pill>
        ) : (
          <Pill>{t('adminAiPipeline.prompts.noCurrent')}</Pill>
        )}
      </div>
      {cur ? (
        <p className="text-xs text-on-surface-variant">
          {t('adminAiPipeline.prompts.versionFrom', { n: cur.version, at: formatDateTime(cur.created_at, i18n.language) || '—' })}
        </p>
      ) : null}
      <div className="space-y-1 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-on-surface-variant">{t('adminAiPipeline.prompts.required')}</span>
          <Placeholders list={p.required_placeholders} />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-on-surface-variant">{t('adminAiPipeline.prompts.allowed')}</span>
          <Placeholders list={p.allowed_placeholders} />
        </div>
      </div>
      {notes.map((n) => (
        <p key={n} className="text-xs text-on-surface-variant">
          {n}
        </p>
      ))}

      <label className="block">
        <span className={LABEL}>system</span>
        <textarea data-ai-prompt-system rows={12} spellCheck={false} value={system} onChange={(e) => setSystem(e.target.value)} className={AREA} />
      </label>
      <label className="block">
        <span className={LABEL}>user_template</span>
        <textarea
          data-ai-prompt-user
          rows={10}
          spellCheck={false}
          value={userTemplate}
          onChange={(e) => setUserTemplate(e.target.value)}
          className={AREA}
        />
      </label>
      <div className="grid gap-3 sm:grid-cols-2">
        <Input
          label={t('adminAiPipeline.prompts.model')}
          placeholder={t('adminAiPipeline.prompts.modelPlaceholder')}
          value={model}
          onChange={(e) => setModel(e.target.value)}
          spellCheck={false}
        />
        <Input
          type="number"
          min={0}
          max={2}
          step={0.1}
          label={t('adminAiPipeline.prompts.temperature')}
          value={temp}
          onChange={(e) => setTemp(e.target.value)}
        />
      </div>

      <div data-ai-prompt-lint className="space-y-1 text-sm">
        {lint.missing.length ? (
          <div className="flex flex-wrap items-center gap-2 text-error">
            <span>{t('adminAiPipeline.prompts.missing')}</span>
            <Placeholders list={lint.missing} />
          </div>
        ) : null}
        {lint.unknown.length ? (
          <div className="flex flex-wrap items-center gap-2 text-error">
            <span>{t('adminAiPipeline.prompts.unknown')}</span>
            <Placeholders list={lint.unknown} />
          </div>
        ) : null}
        {!lint.missing.length && !lint.unknown.length ? (
          <div className="text-on-success-container">{t('adminAiPipeline.prompts.lintOk')}</div>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-2">
        <Button data-ai-prompt-save loading={save.isPending} onClick={onSave}>
          {cur ? t('adminAiPipeline.prompts.saveNext', { n: cur.version + 1 }) : t('adminAiPipeline.prompts.saveFirst')}
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            fill({ system: p.system, user_template: p.user_template, model: '', temperature: p.temperature });
            setNote({ tone: 'hint', text: t('adminAiPipeline.prompts.defaultsFilled') });
          }}
        >
          {t('adminAiPipeline.prompts.defaultsIntoFields')}
        </Button>
        <Button data-ai-prompt-history variant="secondary" aria-expanded={showHistory} onClick={() => setShowHistory((s) => !s)}>
          {t('adminAiPipeline.prompts.history')}
        </Button>
        {cur ? (
          <Button data-ai-prompt-archive variant="danger" loading={archive.isPending} onClick={() => void onArchive()}>
            {t('adminAiPipeline.prompts.archive')}
          </Button>
        ) : null}
      </div>
      <NoteLine note={note} />
      {error ? (
        <div className="space-y-2">
          <ApiErrorText error={error} />
          {stale ? (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span>{t('adminAiPipeline.prompts.staleHint')}</span>
              <Button data-ai-prompt-reread variant="secondary" size="sm" onClick={onReread}>
                {t('adminAiPipeline.prompts.reread')}
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}

      {showHistory ? (
        <div className="space-y-2 border-t border-outline/40 pt-3">
          {history.isLoading ? <p className="text-sm text-on-surface-variant">{t('adminAiPipeline.common.loading')}</p> : null}
          {history.isError ? <ApiErrorText error={history.error} /> : null}
          {history.data ? (
            <>
              <div className="font-semibold">{t('adminAiPipeline.prompts.historyTitle', { n: history.data.length })}</div>
              {!history.data.length ? <p className="text-sm text-on-surface-variant">{t('adminAiPipeline.prompts.historyEmpty')}</p> : null}
              {history.data.map((v) => (
                <VersionRow
                  key={v.id}
                  v={v}
                  onTake={() => {
                    fill(v);
                    setNote({ tone: 'hint', text: t('adminAiPipeline.prompts.taken', { n: v.version }) });
                  }}
                />
              ))}
            </>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}

function VersionRow({ v, onTake }: { v: AiPromptVersion; onTake: () => void }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const lang = i18n.language;
  return (
    <div className="space-y-2 rounded-xl bg-surface-container-low p-3 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold">{`v${v.version}`}</span>
          <span className="text-xs text-on-surface-variant">{formatDateTime(v.created_at, lang) || '—'}</span>
          {v.archived_at ? (
            <Pill>{t('adminAiPipeline.prompts.archivedSince', { at: formatDateTime(v.archived_at, lang) || '—' })}</Pill>
          ) : (
            <Pill variant="success">{t('adminAiPipeline.prompts.active')}</Pill>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="ghost" size="sm" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
            {t('adminAiPipeline.prompts.show')}
          </Button>
          <Button data-ai-version-take={v.id} variant="secondary" size="sm" onClick={onTake}>
            {t('adminAiPipeline.prompts.take')}
          </Button>
        </div>
      </div>
      <div className="text-xs text-on-surface-variant">
        {t('adminAiPipeline.prompts.versionMeta', { model: v.model || t('adminAiPipeline.prompts.serviceModel'), temp: v.temperature })}
      </div>
      {open ? (
        <>
          <div className={LABEL}>system</div>
          <pre className={PRE}>{v.system}</pre>
          <div className={LABEL}>user_template</div>
          <pre className={PRE}>{v.user_template}</pre>
        </>
      ) : null}
    </div>
  );
}
